import { Suspense } from "react";
import { MealPlanAgent } from "@corpuscomplete/core/nutrition/meal-plan-agent";
import { AgentChat } from "@corpuscomplete/core-ui/ai/agent/chat";
import { AgentTools } from "@corpuscomplete/core-ui/ai/agent/tools";
import { MealPlanDetail } from "@corpuscomplete/core-ui/nutrition/meal-plan/detail";
import { StreamingIndicator, ToolError } from "@corpuscomplete/web/modules/common/agent-chat";
import { NutrientTargetsLabel } from "@corpuscomplete/web/components/nutrition/nutrients-label";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from "@corpuscomplete/web-ui/components/item";
import { Label } from "@corpuscomplete/web-ui/components/label";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { ClipboardListIcon, PlusIcon, RotateCcwIcon, Trash2Icon } from "lucide-react";

import { PendingHumanToolCard } from "@corpuscomplete/web/modules/common/agent-chat/tools/pending-human-tool-card";

export function ToolProposePlanPending({
  messageId,
  part,
  isActive,
  isPersisted,
  isQueueInteractive,
  queuePosition,
  queueTotal,
  upNextLabel,
}: {
  messageId: string;
  part: MealPlanDetail.ProposePlanPart;
  isActive: boolean;
  isPersisted: boolean;
  isQueueInteractive?: boolean;
  queuePosition: number;
  queueTotal: number;
  upNextLabel?: string;
}) {
  if (part.state === "input-streaming") {
    return <StreamingIndicator />;
  }

  if (part.state === "output-error") {
    return <ToolError errorText={part.errorText} />;
  }

  return (
    <Suspense
      fallback={
        <PendingHumanToolCard
          icon={<ClipboardListIcon className="h-4 w-4 text-primary" />}
          title="Review Plan"
          progressLabel={AgentTools.formatPendingQueueProgress(queuePosition, queueTotal)}
          upNextLabel={upNextLabel}
          isActive={isActive}
          isPersisted={isPersisted}
          canCancel={false}
          onCancel={() => {}}
          contentHeight={420}
        >
          <Text variant="xs" className="text-muted-foreground">
            Loading plan context...
          </Text>
        </PendingHumanToolCard>
      }
    >
      <ToolProposePlanPendingResolved
        messageId={messageId}
        part={part}
        isActive={isActive}
        isPersisted={isPersisted}
        isQueueInteractive={isQueueInteractive}
        queuePosition={queuePosition}
        queueTotal={queueTotal}
        upNextLabel={upNextLabel}
      />
    </Suspense>
  );
}

function ToolProposePlanPendingResolved({
  messageId,
  part,
  isActive,
  isPersisted,
  isQueueInteractive,
  queuePosition,
  queueTotal,
  upNextLabel,
}: {
  messageId: string;
  part: MealPlanDetail.ProposePlanPart;
  isActive: boolean;
  isPersisted: boolean;
  isQueueInteractive?: boolean;
  queuePosition: number;
  queueTotal: number;
  upNextLabel?: string;
}) {
  const { cancel } = AgentTools.useToolCancel({
    toolCallId: part.toolCallId,
    messageId,
  });
  const { submit, canSubmit = true } = AgentTools.useToolSubmit<MealPlanAgent.Plan>({
    toolName: "proposePlan",
    toolCallId: part.toolCallId,
    messageId,
  });
  const { canEditSession } = AgentChat.useIds();
  const isInteractive =
    (isQueueInteractive ?? (isActive && isPersisted)) && canEditSession && canSubmit;
  const isCancelInteractive = (isQueueInteractive ?? (isActive && isPersisted)) && canEditSession;
  const { session } = MealPlanDetail.useAgentSessionSuspense();
  const nutrientTargetsMode: MealPlanAgent.PlanFormNutrientTargetsMode =
    session?.context != null ? "required" : "disabled";

  const { form, reset, isSyncing } = MealPlanDetail.useProposePlanDraftForm({
    toolCallId: part.toolCallId,
    input: part.input,
    nutrientTargetsMode,
    onSubmit: async ({ value }) => {
      const draft = MealPlanAgent.planFormToDraft(value, nutrientTargetsMode);
      submit(MealPlanAgent.planFromDraft(draft));
    },
  });

  return (
    <PendingHumanToolCard
      icon={<ClipboardListIcon className="h-4 w-4 text-primary" />}
      title="Review Plan"
      progressLabel={AgentTools.formatPendingQueueProgress(queuePosition, queueTotal)}
      upNextLabel={upNextLabel}
      isActive={isActive}
      isPersisted={isPersisted}
      canCancel={part.state === "input-available" && isCancelInteractive}
      onCancel={() => {
        reset();
        cancel();
      }}
      contentHeight={420}
      headerRight={
        isSyncing ? (
          <Text variant="xs" className="text-muted-foreground">
            Saving draft...
          </Text>
        ) : null
      }
      footerActions={
        isActive ? (
          <>
            <Button variant="ghost" size="sm" onClick={reset} disabled={!isInteractive}>
              <RotateCcwIcon className="size-4" />
              <Text variant="sm">Reset</Text>
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => void form.handleSubmit()}
              disabled={!isInteractive}
            >
              <Text variant="sm">Approve</Text>
            </Button>
          </>
        ) : (
          <Text variant="xs" className="text-muted-foreground">
            Waiting for previous step
          </Text>
        )
      }
    >
      <ProposePlanEditor
        form={form}
        disabled={!isInteractive}
        nutrientTargetsMode={nutrientTargetsMode}
      />
    </PendingHumanToolCard>
  );
}

export function ToolProposePlanCompleted({ part }: { part: MealPlanDetail.ProposePlanPart }) {
  if (part.state === "input-streaming" || part.state === "output-error") {
    return null;
  }

  if (AgentTools.isOutputCanceledState(part)) {
    return (
      <div className="mb-4 rounded-lg border border-border bg-muted/30 p-3">
        <Text variant="xs" className="text-muted-foreground">
          Plan review canceled
        </Text>
      </div>
    );
  }

  if (!part.output) {
    return null;
  }

  return (
    <div className="mb-4 flex flex-col gap-2 rounded-lg border border-border bg-muted/30 p-3">
      <Text variant="xs" className="text-muted-foreground">
        Plan approved
      </Text>
      <Text variant="xs" className="text-muted-foreground">
        {part.output.slots.length} slots submitted for generation.
      </Text>
      <div className="space-y-2">
        {part.output.slots.map((slot) => (
          <Item key={slot.slotId} size="xs" variant="outline">
            <ItemContent>
              <ItemTitle>
                {slot.name} - {slot.time}
              </ItemTitle>
              <ItemDescription>
                {formatApprovedSlotSummary(slot.meals.map((meal) => meal.occurrenceIds.length))}
              </ItemDescription>
              {slot.nutrientTargets ? (
                <div className="mt-1">
                  <NutrientTargetsLabel targets={slot.nutrientTargets} abbreviated />
                </div>
              ) : null}
            </ItemContent>
          </Item>
        ))}
      </div>
    </div>
  );
}

export function ProposePlanEditor({
  form,
  disabled,
  nutrientTargetsMode,
}: {
  form: ReturnType<typeof MealPlanDetail.useProposePlanDraftForm>["form"];
  disabled: boolean;
  nutrientTargetsMode: MealPlanAgent.PlanFormNutrientTargetsMode;
}) {
  const showsNutrientTargets = nutrientTargetsMode !== "disabled";
  const requiresNutrientTargets = nutrientTargetsMode === "required";

  return (
    <form.AppForm>
      <form.AppField name="slots" mode="array">
        {(slotsField) => (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <Text variant="xs" className="text-muted-foreground">
                Plan Slots
              </Text>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  slotsField.pushValue({
                    name: "",
                    time: "08:00",
                    nutrientTargets: emptyNutrientTargetsForm(requiresNutrientTargets),
                    meals: [emptyMealForm()],
                  })
                }
                disabled={disabled}
              >
                <PlusIcon className="size-4" />
                <Text variant="sm">Add Slot</Text>
              </Button>
            </div>

            {slotsField.state.meta.errors.length > 0 ? (
              <Text variant="xs" className="text-destructive">
                {slotsField.state.meta.errors.map((error) => error?.message).join(", ")}
              </Text>
            ) : null}

            {slotsField.state.value.map((slot, slotIndex) => (
              <Item size="xs" key={slotIndex} variant="outline">
                <ItemContent>
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <ItemTitle>Slot {slotIndex + 1}</ItemTitle>
                        <ItemDescription>
                          {formatDraftSlotSummary(slot.meals.map((meal) => meal.occurrenceCount))}
                        </ItemDescription>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => slotsField.removeValue(slotIndex)}
                        disabled={disabled}
                      >
                        <Trash2Icon className="size-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <form.AppField name={`slots[${slotIndex}].name`}>
                        {(field) => (
                          <field.Input
                            label="Slot Name"
                            placeholder="Breakfast"
                            disabled={disabled}
                          />
                        )}
                      </form.AppField>
                      <form.AppField name={`slots[${slotIndex}].time`}>
                        {(field) => (
                          <field.Input label="Time" placeholder="08:00" disabled={disabled} />
                        )}
                      </form.AppField>
                    </div>

                    {showsNutrientTargets ? (
                      <div className="space-y-2 rounded-md border border-border/70 p-3">
                        <div className="flex items-center gap-2">
                          <Label>Nutrient Targets</Label>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <form.AppField name={`slots[${slotIndex}].nutrientTargets.energy`}>
                            {(field) => (
                              <field.Input label="Calories" placeholder="500" disabled={disabled} />
                            )}
                          </form.AppField>
                          <form.AppField name={`slots[${slotIndex}].nutrientTargets.protein`}>
                            {(field) => (
                              <field.Input
                                label="Protein"
                                placeholder="30"
                                endText="g"
                                disabled={disabled}
                              />
                            )}
                          </form.AppField>
                          <form.AppField name={`slots[${slotIndex}].nutrientTargets.carbohydrate`}>
                            {(field) => (
                              <field.Input
                                label="Carbohydrate"
                                placeholder="45"
                                endText="g"
                                disabled={disabled}
                              />
                            )}
                          </form.AppField>
                          <form.AppField name={`slots[${slotIndex}].nutrientTargets.fat`}>
                            {(field) => (
                              <field.Input
                                label="Fat"
                                placeholder="20"
                                endText="g"
                                disabled={disabled}
                              />
                            )}
                          </form.AppField>
                        </div>
                      </div>
                    ) : null}

                    <form.AppField name={`slots[${slotIndex}].meals`} mode="array">
                      {(mealsField) => (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <Text variant="xs" className="text-muted-foreground">
                              Meals
                            </Text>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => mealsField.pushValue(emptyMealForm())}
                              disabled={disabled}
                            >
                              <PlusIcon className="size-4" />
                              <Text variant="sm">Add Meal</Text>
                            </Button>
                          </div>

                          {mealsField.state.meta.errors.length > 0 ? (
                            <Text variant="xs" className="text-destructive">
                              {mealsField.state.meta.errors
                                .map((error) => error?.message)
                                .join(", ")}
                            </Text>
                          ) : null}

                          <div className="space-y-2">
                            {mealsField.state.value.map((_meal, mealIndex) => (
                              <div
                                key={mealIndex}
                                className="grid grid-cols-[1fr_120px_auto] items-start gap-2"
                              >
                                <form.AppField
                                  name={`slots[${slotIndex}].meals[${mealIndex}].name`}
                                >
                                  {(field) => (
                                    <field.Input
                                      label={mealIndex === 0 ? "Meal Name" : undefined}
                                      placeholder="Greek yogurt bowl"
                                      disabled={disabled}
                                    />
                                  )}
                                </form.AppField>
                                <form.AppField
                                  name={`slots[${slotIndex}].meals[${mealIndex}].occurrenceCount`}
                                >
                                  {(field) => (
                                    <field.Input
                                      label={mealIndex === 0 ? "Occurrences" : undefined}
                                      placeholder="1"
                                      disabled={disabled}
                                    />
                                  )}
                                </form.AppField>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={mealIndex === 0 ? "mt-6" : "mt-0"}
                                  onClick={() => mealsField.removeValue(mealIndex)}
                                  disabled={disabled}
                                >
                                  <Trash2Icon className="size-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </form.AppField>

                    {showsNutrientTargets ? <NutrientTargetsPreview slot={slot} /> : null}
                  </div>
                </ItemContent>
              </Item>
            ))}
          </div>
        )}
      </form.AppField>
    </form.AppForm>
  );
}

function NutrientTargetsPreview({ slot }: { slot: MealPlanAgent.PlanForm["slots"][number] }) {
  const targets = MealPlanAgent.planFormToDraft({ slots: [slot] }).slots[0]?.nutrientTargets;
  if (!targets) {
    return null;
  }

  return <NutrientTargetsLabel targets={targets} abbreviated />;
}

function emptyMealForm(): MealPlanAgent.PlanForm["slots"][number]["meals"][number] {
  return {
    name: "",
    occurrenceCount: "1",
  };
}

function emptyNutrientTargetsForm(
  enabled: boolean,
): MealPlanAgent.PlanForm["slots"][number]["nutrientTargets"] {
  return {
    enabled,
    energy: "",
    protein: "",
    carbohydrate: "",
    fat: "",
  };
}

function formatDraftSlotSummary(occurrenceCounts: string[]): string {
  const totalOccurrences = occurrenceCounts.reduce(
    (sum, count) => sum + (Number.parseInt(count, 10) || 0),
    0,
  );
  return `${totalOccurrences} occurrences / ${occurrenceCounts.length} unique meals`;
}

function formatApprovedSlotSummary(occurrenceCounts: number[]): string {
  const totalOccurrences = occurrenceCounts.reduce((sum, count) => sum + count, 0);
  return `${totalOccurrences} occurrences / ${occurrenceCounts.length} unique meals`;
}
